<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      v-model="modal.show"
      :title="modalTitle"
      :ok-title="`Submit`"
      :ok-disabled="okButtonDisabled"
      @ok.prevent="handleSubmit(onSubmit)"
      @hidden="onHideModal"
      @show="onShowModal"
    >
      <show-error
        :show="!!error"
        :text="error"
      />
      <content-spinner :show="loading" />
      <b-form
        v-if="!loading"
        @submit.prevent="handleSubmit(onSubmit)"
        @keydown.enter.prevent="handleSubmit(onSubmit)"
      >
        <input-widget
          v-for="field in fields"
          :model="form"
          :field="field"
          :autofocus="field.autofocus"
          :disabled="isEditMode && field.name === 'name'"
          @blur="onInputBlur"
        />
      </b-form>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import InputWidget from '../../../../core/components/complex-table/components/InputWidget';
import ContentSpinner from '../../../../core/components/ContentSpinner';
import ShowError from '../../../../core/components/ShowError';

const {mapState, mapActions} = createNamespacedHelpers('fileManipulationController');

export default {
  name: 'AddEditStorageModal',
  components: {
    ContentSpinner,
    InputWidget,
    ShowError,
  },
  data() {
    return {
      form: {
        name: null,
        username: null,
        password: null,
        ftpServer: null,
        root: null,
        isSFTP: true,
        requiresPPKOrSSH: true,
        sshHostKeyFingerprint: null,
        port: 0,
      },
      fields: {
        name: {
          type: 'text',
          required: false,
          autofocus: true,
          label: 'Name',
          name: 'name',
        },
        username: {
          type: 'text',
          required: false,
          label: 'Username',
          name: 'username',
        },
        password: {
          type: 'text',
          required: false,
          label: 'Password',
          name: 'password',
        },
        ftpServer: {
          type: 'text',
          required: false,
          label: 'FTP Server',
          name: 'ftpServer',
        },
        root: {
          type: 'text',
          required: false,
          label: 'Root',
          name: 'root',
        },
        isSFTP: {
          type: 'checkbox',
          required: false,
          label: 'Is SFTP?',
          name: 'isSFTP',
        },
        requiresPPKOrSSH: {
          type: 'checkbox',
          required: false,
          label: 'Requires PPK or SSH?',
          name: 'requiresPPKOrSSH',
        },
        sshHostKeyFingerprint: {
          type: 'text',
          required: false,
          label: 'SSH Host Key Fingerprint',
          name: 'sshHostKeyFingerprint',
        },
        port: {
          type: 'number',
          required: false,
          label: 'Port',
          name: 'port',
        },
      },
      error: null,
      loading: false,
    };
  },
  computed: {
    ...mapState({
      modal: state => state.addEditModal,
      ftpStorageList: state => state.ftpStorageList,
    }),
    modalTitle() {
      return this.isEditMode ? 'Edit Storage' : 'Add New Storage';
    },
    isEditMode() {
      return this.modal.data.item;
    },
    okButtonDisabled() {
      return this.loading || !!this.error;
    },
  },
  methods: {
    ...mapActions([
      'upsertFtpStorage',
      'hideAddEditModal',
    ]),
    async onSubmit() {
      this.loading = true;

      const params = {
        name: this.form.name,
        config: {
          ...this.form,
        },
      };
      delete params.config.name;

      const action = this.ftpStorageList.find(s => s.name === params.name) ? 'update' : 'create';

      const {success, body} = await this.upsertFtpStorage({params, action});
      if (success) {
        this.$toasted.success(`Storage ${action}d successfully`, {
          duration: TOASTER_DURATION,
        });

        this.hideAddEditModal();
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }

      this.loading = false;
    },
    onShowModal() {
      if (this.isEditMode) {
        this.form.name = this.modal.data.item.name;
        this.form = {...this.form, ...this.modal.data.item.config};
      }
    },
    onHideModal() {
      this.resetForm();
      this.clearErrors();
    },
    resetForm() {
      this.form = {
        name: null,
        username: null,
        password: null,
        ftpServer: null,
        root: null,
        isSFTP: true,
        requiresPPKOrSSH: true,
        sshHostKeyFingerprint: null,
        port: 0,
      };
    },
    clearErrors() {
      this.error = null;
    },
    onInputBlur(e) {
      if (e.target.getAttribute('name')) {
        const storageExist = !!this.ftpStorageList.find(s => s.name === this.form.name);
        if (!this.isEditMode && storageExist) {
          this.error = `Storage with name "${this.form.name}" already exists.`;
        } else {
          this.error = null;
        }
      }
    },
  },
};
</script>

<style scoped>

</style>